import { Injectable, Optional } from "@angular/core";
import { GuardsCheckEnd, NavigationEnd, Router } from "@angular/router";
import { createEffect } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { CORE_ROUTE, CoreState, DialogService } from "@obg/ngx.core";
import { getQueryParams } from "@obg/ngx.global";
import { PlatformService } from "@obg/ngx.global.evo";
import { getActivatedRouteSnapshot, RouteAction, RouteService } from "@obg/ngx.router";
import { asyncScheduler, EMPTY } from "rxjs";
import { concatMap, filter, map, observeOn, tap } from "rxjs/operators";

import { AlternativeMainPageConfig } from "../alternative-main-page/alternative-main-page.config";

@Injectable()
export class DialogEffect {
	openDialog$ = createEffect(
		() =>
			this.router.events.pipe(
				filter(
					(event) =>
						!!this.dialogService &&
						event instanceof GuardsCheckEnd &&
						(event as GuardsCheckEnd).shouldActivate
				),
				concatMap((event: GuardsCheckEnd) => {
					const routeConfig = this.routeService.getConfigFromStateSnapshot(event.state);
					if (!(routeConfig && routeConfig!.data && routeConfig!.data!.openDialog)) {
						return EMPTY;
					}

					const alternativeMainPageEnabled = this.service.getAlternativeMainPageEnabled();
					const urlFragments = [];

					const authDialogRoutes = [
						CORE_ROUTE.auth.resetPasswordTokenOnly,
						CORE_ROUTE.auth.resetPasswordEmailToken,
						CORE_ROUTE.auth.forgotPassword,
					];

					if (alternativeMainPageEnabled && authDialogRoutes.includes(routeConfig.name)) {
						urlFragments.push(this.service.getAlternativeMainPagePath());
					}

					const rootUrlTree = this.router.createUrlTree(urlFragments, {
						queryParamsHandling: "merge",
						queryParams: {
							action: this.platformService.isServer ? routeConfig.name : null,
							returnUrl: null,
							...getQueryParams(event.state.url),
						},
					});

					const state = this.router.getCurrentNavigation()?.extras.state;
					if (state) {
						this.store.dispatch(RouteAction.setAdditionalData(state));
					}

					this.router.navigateByUrl(rootUrlTree, { replaceUrl: true });

					const openRoute$ = this.dialogService.openRoute(routeConfig!.name, {
						data: {
							...getQueryParams(rootUrlTree.toString()),
							...getActivatedRouteSnapshot(event.state)!.params,
						},
					});

					if (!openRoute$) {
						return EMPTY;
					}

					return openRoute$;
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles ?action=xxx query parameter of current URL. Performs navigation to route with name equal to `action`
	 */
	actionQuery$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.router.events.pipe(
					// Listen to NavigationEnd to allow the navigated route to load correctly before navigating. Otherwise there were some problems.
					filter((event) => !!this.dialogService && event instanceof NavigationEnd),
					map((event: NavigationEnd) => {
						const { action, ...queryParams } = getQueryParams(event.url);
						return { action, queryParams };
					}),
					filter(({ action }) => !!action),
					// clear `action` query param. Needed if navigation to `action` route will be cancelled due to guards
					tap(({ queryParams }) => this.store.dispatch(RouteAction.setQueryParams(queryParams))),
					// navigation should be performed asyncronously, due to there can be no two navigations in a row:
					// `RouteAction.setQueryParams` and `RouteAction.navigate` - they both are navigations
					observeOn(asyncScheduler),
					tap(({ action, queryParams }) => {
						this.store.dispatch(
							RouteAction.navigate(action, undefined, {
								queryParams,
							})
						);
					})
				),
			{ dispatch: false }
		)
	);

	constructor(
		private router: Router,
		private store: Store<CoreState>,
		private routeService: RouteService,
		private platformService: PlatformService,
		private service: AlternativeMainPageConfig,
		@Optional() private dialogService: DialogService
	) {}
}
